﻿import { useEffect, useMemo } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, DataTable,
  EmptyState, Spinner, Layout, BlockStack, InlineStack,
  Divider, Pagination, InlineGrid,
} from '@shopify/polaris';
import { WrenchIcon, ClipboardChecklistIcon } from '@shopify/polaris-icons';
import { useAutoFix } from '../../hooks/useAutoFix';
import type { AutoFixRun, AutoFixAction } from '../../api/autoFix';
import { StatCard } from '../shared/StatCard';

function statusBadge(status: AutoFixRun['status']) {
  const map: Record<string, 'info' | 'success' | 'critical' | 'warning'> = {
    ready: 'info',
    applying: 'warning',
    completed: 'success',
    failed: 'critical',
  };
  return <Badge tone={map[status] ?? 'info'}>{status}</Badge>;
}

function actionStatusBadge(status: AutoFixAction['status']) {
  const map: Record<string, 'success' | 'critical' | 'info' | 'warning'> = {
    pending: 'info',
    applied: 'success',
    failed: 'critical',
    skipped: 'warning',
  };
  return <Badge tone={map[status] ?? 'info'}>{status}</Badge>;
}

interface Props {
  onBack?: () => void;
}

export function AutoFixPanel(_props: Props) {
  const {
    runs, activeRun, loading, planning, applying, error, page, lastPage,
    fetchRuns, createPlan, applyRun, deleteRun, setActiveRun, setError,
  } = useAutoFix();

  useEffect(() => {
    fetchRuns(1);
  }, []);

  const totalApplied = useMemo(() => runs.reduce((sum, r) => sum + r.applied_count, 0), [runs]);

  if (activeRun) {
    const actions = activeRun.actions ?? [];
    const rows = actions.map((a) => {
      let newValueDisplay = `${a.new_value.slice(0, 80)}${a.new_value.length > 80 ? '…' : ''}`;
      if (a.fix_type === 'create_legal_page') {
        try {
          const parsed = JSON.parse(a.new_value);
          newValueDisplay = `New draft page: "${parsed.title}"`;
        } catch {
          // fall back to raw truncated string above
        }
      }

      return [
        a.product_title,
        a.fix_type_label,
        a.old_value ? (
          <Text as="span" tone="subdued" variant="bodySm">{a.old_value.slice(0, 60)}{a.old_value.length > 60 ? '…' : ''}</Text>
        ) : <Text as="span" tone="subdued" variant="bodySm">—</Text>,
        <Text as="span" variant="bodySm">{newValueDisplay}</Text>,
        a.status === 'failed' && a.error_message ? (
          <BlockStack gap="100">
            {actionStatusBadge(a.status)}
            <Text as="span" tone="critical" variant="bodySm">{a.error_message}</Text>
          </BlockStack>
        ) : actionStatusBadge(a.status),
      ];
    });

    return (
      <Page fullWidth
        title={`Fix Plan #${activeRun.id}`}
        subtitle={`${activeRun.planned_count} fixes planned`}
        backAction={{ content: 'All Runs', onAction: () => setActiveRun(null) }}
      >
        <Layout>
          <Layout.Section>
            <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            )}
            <Card>
              <BlockStack gap="400">
                <InlineStack gap="400" align="space-between">
                  <InlineStack gap="200">
                    {statusBadge(activeRun.status)}
                    <Text as="span" variant="bodySm" tone="subdued">
                      {activeRun.applied_count}/{activeRun.planned_count} applied
                      {activeRun.failed_count > 0 && ` · ${activeRun.failed_count} failed`}
                    </Text>
                  </InlineStack>
                  {activeRun.is_ready && (
                    <Button
                      variant="primary"
                      loading={applying}
                      onClick={() => applyRun(activeRun.id)}
                    >
                      Apply All Fixes to Shopify
                    </Button>
                  )}
                </InlineStack>

                {activeRun.status === 'completed' && (
                  <Banner tone="success">
                    <p>All {activeRun.applied_count} fixes applied successfully to your Shopify store.</p>
                  </Banner>
                )}
                {activeRun.status === 'failed' && (
                  <Banner tone="warning">
                    <p>
                      {activeRun.applied_count} fixes applied, {activeRun.failed_count} failed.
                      Check individual action errors below.
                    </p>
                  </Banner>
                )}

                <Divider />

                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                  headings={['Item', 'Fix Type', 'Current Value', 'New Value', 'Status']}
                  rows={rows}
                  truncate
                />
              </BlockStack>
            </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page fullWidth
      title="Auto Fix"
      subtitle="Let AI automatically fix SEO titles, meta descriptions, missing product descriptions, page SEO, and missing legal pages"
      primaryAction={{
        content: 'Create Fix Plan',
        loading: planning,
        onAction: async () => {
          const run = await createPlan();
          if (run) setActiveRun(run);
        },
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
            <StatCard label="Fix Runs" value={String(runs.length)} icon={WrenchIcon} iconColor="teal" />
            <StatCard label="Fixes Applied" value={String(totalApplied)} icon={ClipboardChecklistIcon} iconColor="green" />
          </InlineGrid>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Fix Runs</Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                Each run scans your store for missing SEO titles, meta descriptions, product
                descriptions, page SEO, and missing legal pages (Privacy, Refund, Terms, Shipping),
                generates AI-powered drafts, then applies them to Shopify once you approve. New legal
                pages are created as unpublished drafts — always review AI-drafted legal content before publishing.
              </Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : runs.length === 0 ? (
                <EmptyState
                  heading="No fix runs yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{
                    content: 'Create Fix Plan',
                    loading: planning,
                    onAction: async () => {
                      const run = await createPlan();
                      if (run) setActiveRun(run);
                    },
                  }}
                >
                  <p>Click "Create Fix Plan" to let AI scan and generate fixes for your store's content gaps.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['numeric', 'text', 'numeric', 'numeric', 'text', 'text']}
                  headings={['ID', 'Status', 'Planned', 'Applied', 'Cost', 'Actions']}
                  rows={runs.map((r) => [
                    r.id,
                    statusBadge(r.status),
                    r.planned_count,
                    r.applied_count,
                    `$${r.cost_usd.toFixed(4)}`,
                    <InlineStack gap="200">
                      <Button variant="plain" size="slim" onClick={() => setActiveRun(r)}>
                        View
                      </Button>
                      <Button
                        variant="plain"
                        tone="critical"
                        size="slim"
                        onClick={() => deleteRun(r.id)}
                      >
                        Delete
                      </Button>
                    </InlineStack>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchRuns(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchRuns(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
